import { ComponentProps } from 'react'

import { DetailsItemsList } from '@factorialco/f0-react/dist/experimental'

import buildUrl from 'modules/core/lib/buildUrl'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import { formatProjectHours } from 'modules/project_management/lib/formatHours'

import { TaskDetail } from 'modules/tasks/components/TaskElement/useTaskDetail'
import { statusOption } from 'modules/tasks/lib/status'

interface Props {
  task: TaskDetail
  stepName?: string | null
  mandatory?: boolean
  hasProjectsTasks?: boolean
  hasProjectsTasksTimeTracking?: boolean
  canManageProjectTask?: boolean
}

type DetailItem = ComponentProps<typeof DetailsItemsList>['details'][number]

const splitName = (fullName: string) => {
  const idx = fullName.indexOf(' ')
  if (idx === -1) return { firstName: fullName, lastName: '' }
  return { firstName: fullName.slice(0, idx), lastName: fullName.slice(idx + 1) }
}

const Info = ({
  task,
  mandatory,
  stepName,
  hasProjectsTasks,
  hasProjectsTasksTimeTracking,
  canManageProjectTask,
}: Props) => {
  const author = 'author' in task ? task.author : undefined
  const taskCreatedFromProject = task.identifier === 'project'
  const isProjectTask = task.projectTask != null

  const completedAt =
    'completedAt' in task && task.completedAt
      ? i18nDateToString(stringToDate(task.completedAt, 'backend_datetime'), 'ui')
      : null

  const completedBy = 'completedBy' in task && task.completedBy ? task.completedBy : undefined
  const selectedStatusOption = task.status ? statusOption(task.status) : undefined

  const dueOnFormatted = task.dueOn
    ? i18nDateToString(stringToDate(task.dueOn, 'backend_datetime'), 'ui')
    : null

  const dueOnContent: DetailItem['content'] = dueOnFormatted
    ? { type: 'item', text: dueOnFormatted }
    : { type: 'status-tag', text: i18n.t('tasks.labels.no_due_date'), variant: 'neutral' }

  const projectName = task.projectTask?.imputableProject?.name
  const phaseName = task.projectTask?.subproject?.name

  const details: DetailItem[] = []

  // Status
  if (selectedStatusOption) {
    details.push({
      title: i18n.t('tasks.labels.status'),
      content: {
        type: 'status-tag',
        text: selectedStatusOption.title,
        variant: selectedStatusOption.statusType,
      },
    })
  }

  // Workflow task for (affectees)
  if (task.affectees && task.affectees.length > 0) {
    details.push({
      title: i18n.t('tasks.labels.workflow_task_for'),
      content: task.affectees.map((a) => ({
        type: 'person' as const,
        ...splitName(a.fullName),
        avatarUrl: a.avatar ? buildUrl(a.avatar) : undefined,
      })),
    })
  }

  // Due date (always shown)
  details.push({
    title: i18n.t('tasks.labels.due_date'),
    content: dueOnContent,
  })

  // Mandatory (not for project tasks)
  if (!taskCreatedFromProject) {
    details.push({
      title: i18n.t('tasks.labels.mandatory_task'),
      content: {
        type: 'status-tag',
        text: mandatory ? i18n.t('tasks.labels.yes') : i18n.t('tasks.labels.no'),
        variant: mandatory ? 'warning' : 'positive',
      },
    })
  }

  // Step (not for project tasks)
  if (!taskCreatedFromProject && stepName) {
    details.push({
      title: i18n.t('tasks.labels.step'),
      content: { type: 'item', text: stepName },
    })
  }

  if (isProjectTask && hasProjectsTasks && projectName) {
    details.push({
      title: i18n.t('tasks.labels.project'),
      content: { type: 'item', text: projectName },
    })
  }

  if (isProjectTask && hasProjectsTasks && phaseName) {
    details.push({
      title: i18n.t('tasks.labels.phase'),
      content: { type: 'item', text: phaseName },
    })
  }

  // Assignees
  if (task.assignees && task.assignees.length > 0) {
    const remainingAssignees = Math.max(0, task.assigneesCount - task.assignees.length)
    const content =
      remainingAssignees === 0
        ? task.assignees.map((a) => ({
            type: 'person' as const,
            ...splitName(a.fullName),
            avatarUrl: a.avatar ? buildUrl(a.avatar) : undefined,
          }))
        : {
            type: 'avatar-list' as const,
            avatarList: {
              type: 'person' as const,
              avatars: task.assignees.map((a) => ({
                ...splitName(a.fullName),
                src: a.avatar ? buildUrl(a.avatar) : undefined,
              })),
              size: 'sm' as const,
              max: task.assignees.length,
              remainingCount: remainingAssignees,
            },
          }
    details.push({
      title: i18n.t('tasks.labels.assignees'),
      content,
    })
  }

  // Created by
  if (author) {
    details.push({
      title: i18n.t('tasks.labels.created_by'),
      content: {
        type: 'person',
        ...splitName(author.fullName),
        avatarUrl: author.avatar ? buildUrl(author.avatar) : undefined,
      },
    })
  }

  // Completed at
  if (completedAt) {
    details.push({
      title: i18n.t('tasks.labels.completed_at'),
      content: { type: 'item', text: completedAt },
    })
  }

  // Completed by
  if (completedBy) {
    details.push({
      title: i18n.t('tasks.labels.completed_by'),
      content: {
        type: 'person',
        ...splitName(completedBy.fullName),
        avatarUrl: completedBy.avatar ? buildUrl(completedBy.avatar) : undefined,
      },
    })
  }

  // Total time tracked
  if (
    hasProjectsTasksTimeTracking &&
    canManageProjectTask &&
    taskCreatedFromProject &&
    task.projectTask != null
  ) {
    details.push({
      title: i18n.t('tasks.labels.total_time_tracked'),
      content: {
        type: 'item',
        text: formatProjectHours(task.projectTask.imputedMinutes ?? null),
      },
    })
  }

  return <DetailsItemsList title={i18n.t('tasks.labels.details')} details={details} />
}

export default Info
